import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { useIntl } from 'ha/i18n';
import {
  Checkbox,
  FormControlLabel,
  FormRaw,
  FormSection,
  Grid,
  Typography,
  units,
} from '@hbf/dsl';
import { Field } from 'react-final-form';
import { UserDocumentCategory } from '@ha/api/v2/types/UserDocument';

const useStyles = makeStyles()(theme => ({
  checkboxSubLabel: {
    paddingLeft: theme.spacing(4),
    fontSize: units.rem(0.875),
    marginTop: theme.spacing(-1.25),
  },
  subtitle: {
    padding: theme.spacing(2, 0),
  },
}));

export interface Props {
  showHeader?: boolean;
}

const RequiredDocuments: React.FC<Props> = ({ showHeader = false }) => {
  const { T } = useIntl();
  const { classes } = useStyles();

  return (
    <FormSection
      title={
        showHeader ? (
          <React.Fragment>
            <Typography.H2>
              {T('listings.create.required_docs.title')}
            </Typography.H2>
            <Typography.Paragraph className={classes.subtitle}>
              {T('listings.create.required_docs.subtitle')}
            </Typography.Paragraph>
          </React.Fragment>
        ) : undefined
      }
    >
      <Grid container spacing={1} direction="column">
        <FormRaw>
          <Field
            name={`requiredDocuments.${UserDocumentCategory.identification}`}
            type="checkbox"
          >
            {({ input }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test-locator="Required Documents / Identification"
                    id={`requiredDocuments.${UserDocumentCategory.identification}`}
                    color="primary"
                    name={input.name}
                    onChange={input.onChange}
                    value={input.value}
                    checked={input.checked}
                  />
                }
                label={T('listings.create.required_docs.id_visa')}
              />
            )}
          </Field>

          <Typography.HelperText
            className={classes.checkboxSubLabel}
            color="muted"
          >
            {T('listings.create.required_docs.id_visa.description')}
          </Typography.HelperText>
        </FormRaw>

        <FormRaw>
          <Field
            name={`requiredDocuments.${UserDocumentCategory.proofOfOccupation}`}
            type="checkbox"
          >
            {({ input }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test-locator="Required Documents / Proof of Occupation"
                    id={`requiredDocuments.${UserDocumentCategory.proofOfOccupation}`}
                    color="primary"
                    name={input.name}
                    onChange={input.onChange}
                    value={input.value}
                    checked={input.checked}
                  />
                }
                label={T('listings.create.required_docs.occupation')}
              />
            )}
          </Field>

          <Typography.HelperText
            className={classes.checkboxSubLabel}
            color="muted"
          >
            {T('listings.create.required_docs.occupation.description')}
          </Typography.HelperText>
        </FormRaw>

        <FormRaw>
          <Field
            name={`requiredDocuments.${UserDocumentCategory.proofOfIncome}`}
            type="checkbox"
          >
            {({ input }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    data-test-locator="Required Documents / Proof of Income"
                    id={`requiredDocuments.${UserDocumentCategory.proofOfIncome}`}
                    color="primary"
                    name={input.name}
                    onChange={input.onChange}
                    checked={input.checked}
                    value={input.value}
                  />
                }
                label={T('listings.create.required_docs.income_proof')}
              />
            )}
          </Field>

          <Typography.HelperText
            className={classes.checkboxSubLabel}
            color="muted"
          >
            {T('listings.create.required_docs.income_proof.description')}
          </Typography.HelperText>
        </FormRaw>
      </Grid>
    </FormSection>
  );
};

export { RequiredDocuments };
